/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useMemo} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getFileHistoryCurrent} from '@ion/core/src/services/Connect';
import {GenericObject} from '@ion/core/src/types/entities';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseFileHistoryCurrentResult = UseDefaultQueryResult & {
  currentFileHistory: GenericObject[];
  refresh: () => void;
};

/**
 * Retrieves the device current file history for the given device esn.
 *
 * ```typescript
 *
 * import {useFileHistoryCurrent} from '@ion/query/src/hooks/';
 *
 *  ...
 *
 *  const {currentFileHistory} = useFileHistoryCurrent();
 *
 * ```
 *
 * import {useFileHistoryCurrent} from '@ion/query/src/hooks/';
 * @param identifier of the device to retrieve file history for
 * @param staleTime how long the query should hold onto details before re-fetching
 * @returns useCurrentFileHistory containing the query results and the file history.
 */
export const useFileHistoryCurrent = (
  identifier: string,
  staleTime = getStaleTime(1, false, false)
): UseFileHistoryCurrentResult => {
  const queryClient = useQueryClient();
  const key         = useMemo(() => (
    createKey(QueryKey.FILE_HISTORY_CURRENT, identifier)
  ), [identifier]);
  const query       = useQuery(key, async() => {
    const results = await getFileHistoryCurrent(identifier);
    return results;
  }, {
    staleTime : staleTime as number,
    enabled   : !!identifier
  });

  useEffect(() => {
    if (!query.data && identifier && !query.isError) {
      query.refetch();
    }
  }, [query, identifier]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(key);
  }, [key]);

  return createQueryResult(query, {
    currentFileHistory : query.data || [],
    refresh
  }) as UseFileHistoryCurrentResult;
};

export default useFileHistoryCurrent;
